/**
 * levelFromPoints — the 60-level trader ladder, client-side.
 *
 * WHY THIS EXISTS. GET /api/listings hands over each seller's
 * totalAccumulatedPoints (listings.ts:424) and says, in its own select comment,
 * that the CLIENT computes the level from it (listings.ts:415-418). Mobile does
 * exactly that (TraderBadge → LevelBadge, over constants/levels.ts). The web
 * detail seller card and the public profile chip (and, until 2026-09-10, the
 * card overlay, which no longer prints a tier) instead printed the raw
 * trader_level enum word ("rookie") and pinned every seller to level 1 — see
 * mapSellerToUser in floorTransport.ts for the before and after.
 *
 * WHY A MIRROR AND NOT AN IMPORT. The function already exists in
 * lib/bazaar-levels (getLevelFromPoints). This package cannot reach it:
 * @workspace/bazaar-levels is not one of support's workspace dependencies, and
 * adding it is a pnpm-lock.yaml change — release lane, Nick's (carded, with
 * the note that this file collapses to a re-export the day the line lands).
 * artifacts/mobile/constants/levels.ts is the same table for the same reason.
 *
 * 🔑 PINNED TO THE SHARED SOURCE, NOT TRUSTED. levelFromPoints.test.ts reads
 * lib/bazaar-levels/src/index.ts by relative path and fails if a single
 * threshold, MAX_LEVEL, or any level over a sweep of points disagrees. Drift
 * between the two tables is a red, never a quiet second opinion.
 */

/** LEVEL_THRESHOLDS[i] = cumulative total_accumulated_points to reach level i+1.
 *  Level 1 = 0 pts (everyone starts here), level 60 = 4,084,700 pts (max). */
export const LEVEL_THRESHOLDS: readonly number[] = [
  0, 400, 1_300, 2_700, 4_800, 7_600, 11_200, 15_700, 21_100, 27_600,
  35_200, 44_000, 54_100, 65_500, 78_400, 92_800, 108_800, 126_500, 145_900, 167_200,
  190_400, 215_600, 242_900, 272_300, 304_000, 338_000, 374_400, 413_300, 454_700, 499_000,
  546_400, 597_200, 651_700, 710_300, 773_100, 840_200, 911_800, 987_900, 1_068_700, 1_154_400,
  1_245_100, 1_340_900, 1_441_900, 1_548_200, 1_660_000, 1_777_500, 1_900_700, 2_029_800, 2_164_900, 2_306_100,
  2_453_600, 2_607_500, 2_767_900, 2_935_000, 3_108_900, 3_289_700, 3_477_600, 3_672_600, 3_874_900, 4_084_700,
];

export const MAX_LEVEL = 60;

/**
 * The 1-based level (1–60) for a lifetime points total.
 *
 * Absent, null, or non-finite points read as level 1: that is where every
 * account starts, and it is the value the previous hard-coded `lvl: 1` gave
 * every seller — so a row from a cache older than the field renders exactly as
 * it did before this existed, never as "undefined" or a tier that matches no
 * band. Negative points are impossible on the wire and also read as 1.
 */
export function getLevelFromPoints(pts: number | null | undefined): number {
  if (typeof pts !== "number" || !Number.isFinite(pts)) return 1;
  for (let i = MAX_LEVEL - 1; i >= 0; i--) {
    if (pts >= LEVEL_THRESHOLDS[i]) return i + 1;
  }
  return 1;
}
